import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"
import { ReelItemProps } from "../../../entities/ReelsEntity"

type ReelsPageState = {
  items: ReelItemProps[]
  lastFetchTime: number | null
  loadId: string
  page: number
}

type ReelsState = {
  dataByPath: Record<string, ReelsPageState>
  appendItems: (path: string, items?: ReelItemProps[]) => void
  clearData: (path: string) => void
  setLastSeenPage: (path: string, page: number) => void
}

export const buildReelsStoreKey = (
  path: string,
  curationId?: string,
  curationItemId?: string,
) => {
  if (!curationId && !curationItemId) {
    return path
  }

  const params = new URLSearchParams()

  if (curationId) {
    params.set("curationId", curationId)
  }

  if (curationItemId) {
    params.set("curationItemId", curationItemId)
  }

  const query = params.toString()

  return query ? `${path}?${query}` : path
}

export const useReelsStore = create<ReelsState>()(
  persist(
    (set) => ({
      dataByPath: {},
      appendItems: (path, items = []) => {
        return set((state) => {
          const prevItems = state.dataByPath[path]?.items ?? []
          const mergedItems = prevItems.slice()

          // 폴더 릴스인 경우 last-item이 올바른 순서(맨 마지막)를 유지하도록 처리
          const isFolder = path.includes("/reels/folder")
          const hasLastItem = items.some((item) => item.id === "last-item")

          if (isFolder && hasLastItem) {
            // 기존 last-item 제거 (있다면)
            const existingLastItemIndex = mergedItems.findIndex(
              (item) => item.id === "last-item",
            )
            if (existingLastItemIndex !== -1) {
              mergedItems.splice(existingLastItemIndex, 1)
            }
          }

          // 중복 제거 로직 없이 모든 아이템 추가
          for (const it of items) {
            if (!it || it.id === undefined || it.id === null) continue
            mergedItems.push(it)
          }

          return {
            dataByPath: {
              ...state.dataByPath,
              [path]: {
                ...state.dataByPath[path],
                items: mergedItems,
                lastFetchTime: mergedItems.length > 0 ? Date.now() : null,
                loadId: "",
                page: state.dataByPath[path]?.page ?? 0,
              },
            },
          }
        })
      },
      clearData: (path) =>
        set((state) => ({
          dataByPath: {
            ...state.dataByPath,
            [path]: {
              items: [],
              lastFetchTime: null,
              loadId: "",
              page: 0,
            },
          },
        })),
      setLastSeenPage: (path, page) =>
        set((state) => ({
          dataByPath: {
            ...state.dataByPath,
            [path]: {
              ...state.dataByPath[path],
              page,
            },
          },
        })),
    }),
    {
      name: "reels-store",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
)
